import { useCallback, useEffect, useMemo, useState } from 'react';
import { Column, Table } from '@tanstack/react-table';

import { FileUploadDTO } from '@/types/api-types';

import IconAsc from '@/assets/svg/icon-asc.svg?react';
import IconDesc from '@/assets/svg/icon-desc.svg?react';

import { cn } from '@/lib/utils';

import { useFiltersUploadStore } from '@/stores/filtersUploadStore';

import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';

interface Props {
  table: Table<FileUploadDTO>;
  label: string;
  column: Column<FileUploadDTO>;
  sortBy: 'asc' | 'desc';
  onSortChange: (sortBy: 'asc' | 'desc') => void;
  onClear: () => void;
  onApply: () => void;
  disabledOptions?: boolean;
}

export const FilterWidgetUploadComponent = ({
  table,
  // label,
  column,
  sortBy,
  onSortChange,
  onClear,
  onApply,
  disabledOptions,
}: Props) => {
  const [inputValue, setInputValue] = useState<string>('');

  const [options, setOptions] = useState<{ label: string; value: string }[]>(
    [],
  );

  const { clearFilterByIds, setFilterByIds, filterByIds } =
    useFiltersUploadStore();

  const [selectedOptions, setSelectedOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const handleApply = async () => {
    setFilterByIds({
      ...filterByIds,
      [column.id]: selectedOptions.map((option) => option.value),
    });
    onApply();
  };

  const handleClear = (field: keyof FileUploadDTO) => () => {
    setSelectedOptions([]);
    clearFilterByIds(field);
    onClear();
  };

  const handleSortChange = (sortBy: 'asc' | 'desc') => {
    onSortChange(sortBy);
  };
  const setSelected = useCallback(() => {
    setSelectedOptions(
      filterByIds[column.id as keyof FileUploadDTO]?.map((value) => ({
        label: value,
        value: value,
      })) || [],
    );
  }, [filterByIds]);

  useEffect(() => {
    setOptions(
      table.getRowModel().rows.map((row) => ({
        label: row.original[column.id as keyof FileUploadDTO] as string,
        value: row.original[column.id as keyof FileUploadDTO] as string,
      })),
    );

    setSelected();
  }, []);

  const filterOptions = useMemo(
    () =>
      options
        .filter(
          (option) =>
            !inputValue ||
            option.label.toLowerCase().includes(inputValue.toLowerCase()),
        )
        .sort((a, b) => {
          if (selectedOptions.some((option) => option.value === a.value)) {
            return -1;
          }
          if (selectedOptions.some((option) => option.value === b.value)) {
            return 1;
          }
          return 0;
        }),
    [options, inputValue, selectedOptions],
  );

  const renderContent = () => {
    switch (column.id) {
      default:
        return (
          <>
            <div className="flex flex-col px-1">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Параметр фільтру"
              />
            </div>
            <div className="mx-1 flex max-h-[240px] min-h-[80px] flex-col overflow-y-auto rounded-md border border-gray-200 p-4">
              {filterOptions.map((option) => (
                <label
                  key={option.value.toString()}
                  className="flex select-none items-center gap-2 hover:cursor-pointer hover:underline"
                >
                  <Checkbox
                    value={option.value.toString()}
                    onCheckedChange={(checked) => {
                      setSelectedOptions((prev) =>
                        checked
                          ? [...prev, option]
                          : prev.filter((o) => o.value !== option.value),
                      );
                    }}
                    checked={selectedOptions.some(
                      (o) => o.value === option.value,
                    )}
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          </>
        );
    }
  };

  return (
    <div
      className={cn('flex flex-col gap-2 px-1 pb-3', disabledOptions && 'py-0')}
    >
      <div
        className={cn(
          'flex flex-col gap-2.5 border-b border-separator px-4 pb-2 pt-3',
          disabledOptions && 'border-none py-3',
        )}
      >
        <button
          className={`flex items-center gap-1 ${sortBy === 'asc' ? 'text-primary' : ''}`}
          onClick={() => handleSortChange('asc')}
        >
          <IconAsc className="h-4 w-4" />
          <span className="text-sm">Сортування за зростанням</span>
        </button>
        <button
          className={`flex items-center gap-1 ${sortBy === 'desc' ? 'text-primary' : ''}`}
          onClick={() => handleSortChange('desc')}
        >
          <IconDesc className="h-4 w-4 rotate-180" />
          <span className="text-sm">Сортування за спаданням</span>
        </button>
      </div>
      {!disabledOptions ? (
        <>
          <>{renderContent()}</>
          <div className="flex justify-between gap-[50px] px-1">
            <Button
              variant="destructive"
              className="h-[40px] w-[120px]"
              onClick={handleClear(column.id as keyof FileUploadDTO)}
            >
              Очистити
            </Button>
            <Button
              variant="outline"
              className="h-[40px] w-[120px]"
              onClick={handleApply}
            >
              Застосувати
            </Button>
          </div>
        </>
      ) : null}
    </div>
  );
};
